// File: src/pages/cookie/page.tsx
import { useEffect, useState } from 'react';
import Button from '../../components/base/Button';
import { updateConsentDenied, updateConsentGranted } from '../../lib/gtag';

type ConsentStatus = 'accepted' | 'rejected' | 'unset';

const STORAGE_KEY = 'abschlepp1-cookie-consent-v1';

const CookieSettingsPage: React.FC = () => {
  const [status, setStatus] = useState<ConsentStatus>('unset');

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'accepted' || stored === 'rejected') {
      setStatus(stored);
      if (stored === 'accepted') {
        updateConsentGranted();
      } else {
        updateConsentDenied();
      }
    } else {
      setStatus('unset');
      updateConsentDenied();
    }
  }, []);

  const handleAccept = () => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(STORAGE_KEY, 'accepted');
    setStatus('accepted');
    updateConsentGranted();
  };

  const handleReject = () => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(STORAGE_KEY, 'rejected');
    setStatus('rejected');
    updateConsentDenied();
  };

  const handleReset = () => {
    if (typeof window === 'undefined') return;
    window.localStorage.removeItem(STORAGE_KEY);
    setStatus('unset');
    updateConsentDenied();
  };

  const getStatusLabel = () => {
    switch (status) {
      case 'accepted':
        return 'Alle optionalen Cookies akzeptiert.';
      case 'rejected':
        return 'Nur technisch notwendige Cookies aktiv.';
      default:
        return 'Noch keine Cookie-Entscheidung gespeichert.';
    }
  };

  return (
    <main className="pt-24 pb-20 bg-white">
        <section className="py-12 sm:py-16 bg-white" aria-labelledby="cookie-heading">
          <div className="max-w-4xl mx-auto px-6">
            <p
              className="text-xs tracking-[0.22em] uppercase mb-4 text-[#0F3D8C]"
            >
              Cookies &amp; Tracking
            </p>

            <h1
              id="cookie-heading"
              className="text-3xl sm:text-4xl md:text-5xl font-semibold mb-4 leading-tight text-[#0F172A]"
            >
              Cookie-Einstellungen
            </h1>

            <p
              className="text-base sm:text-lg leading-relaxed text-[#0F172A]/85 mb-6"
            >
              Hier kannst du deine Entscheidung zur Verwendung von Cookies und ähnlichen Technologien anpassen. Technisch notwendige Cookies verwenden wir, um die Seite sicher und stabil zu betreiben. Optionale Technologien für Marketingzwecke – aktuell Google Ads Conversion-Tracking für Telefon‑Klicks – werden nur aktiviert, wenn du zustimmst. Ohne Zustimmung wird kein entsprechendes Tracking geladen.
            </p>

            <div className="mb-8 rounded-3xl border border-[#0F3D8C]/15 bg-white px-6 py-5 shadow-[0_10px_25px_rgba(15,23,42,0.06)]">
              <h2
                className="text-sm md:text-base font-medium mb-2 text-[#0F172A]"
              >
                Aktueller Status
              </h2>
              <p
                className="text-sm md:text-base text-[#0F172A]/85"
              >
                {getStatusLabel()}
              </p>
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              <div className="rounded-3xl border border-[#0F3D8C]/15 bg-white px-5 py-5 shadow-[0_10px_25px_rgba(15,23,42,0.06)]">
                <h3
                  className="text-xs tracking-[0.18em] uppercase mb-2 text-[#0F3D8C]"
                >
                  Nur notwendige Cookies
                </h3>
                <p
                  className="text-sm text-[#0F172A]/85 mb-4"
                >
                  Es werden nur Cookies eingesetzt, die für den technischen Betrieb der Seite
                  erforderlich sind. Keine Statistik- oder Marketing-Cookies.
                </p>
                <Button
                  size="sm"
                  onClick={handleReject}
                  className="w-full text-[0.7rem] sm:text-xs tracking-[0.18em] uppercase px-4 py-2.5 rounded-full"
                >
                  Auswahl speichern
                </Button>
              </div>

              <div className="rounded-3xl border border-[#0F3D8C]/15 bg-white px-5 py-5 shadow-[0_10px_25px_rgba(15,23,42,0.06)]">
                <h3
                  className="text-xs tracking-[0.18em] uppercase mb-2 text-[#0F3D8C]"
                >
                  Alle Cookies akzeptieren
                </h3>
                <p
                  className="text-sm text-[#0F172A]/85 mb-4"
                >
                  Optional erlaubst du zusätzlich Cookies für Statistik und Marketing, sofern wir
                  solche Tools einsetzen.
                </p>
                <Button
                  size="sm"
                  onClick={handleAccept}
                  className="w-full text-[0.7rem] sm:text-xs tracking-[0.18em] uppercase px-4 py-2.5 rounded-full"
                >
                  Alle akzeptieren
                </Button>
              </div>

              <div className="rounded-3xl border border-[#0F3D8C]/15 bg-white px-5 py-5 shadow-[0_10px_25px_rgba(15,23,42,0.06)]">
                <h3
                  className="text-xs tracking-[0.18em] uppercase mb-2 text-[#0F3D8C]"
                >
                  Entscheidung zurücksetzen
                </h3>
                <p
                  className="text-sm text-[#0F172A]/85 mb-4"
                >
                  Löscht die aktuelle Entscheidung im Browser. Beim nächsten Seitenaufruf
                  erscheint das Cookie-Banner erneut.
                </p>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={handleReset}
                  className="w-full text-[0.7rem] sm:text-xs tracking-[0.18em] uppercase px-4 py-2.5 rounded-full"
                >
                  Entscheidung löschen
                </Button>
              </div>
            </div>

            <p
              className="mt-8 text-xs sm:text-sm text-[#0F172A]/70"
            >
              Hinweis: Diese Seite ersetzt keine rechtliche Beratung. Für individuelle Anforderungen
              (z. B. bei komplexen Tracking-Setups, E-Commerce oder mehreren Drittanbietern) kann es
              sinnvoll sein, eine auf IT-Recht spezialisierte Kanzlei einzubeziehen.
            </p>
          </div>
        </section>
    </main>
  );
};

export default CookieSettingsPage;
